import React, { useCallback, useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { auth } from '../../firebase';

const STORAGE_KEY = 'admin.sidebarCollapsed';

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const writeCollapsed = (collapsed) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch (_) {
    // ignore
  }
};

const Icon = ({ children, className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {children}
  </svg>
);

const DashboardIcon = () => (
  <Icon>
    <path d="M3 3v18h18" />
    <path d="M7 15l4-4 4 4 5-6" />
  </Icon>
);

const ModelsIcon = () => (
  <Icon>
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
    <path d="M12 12l8-4.5" />
    <path d="M12 12v9" />
    <path d="M12 12L4 7.5" />
  </Icon>
);

const GivingIcon = () => (
  <Icon>
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8z" />
  </Icon>
);

const ChevronIcon = ({ direction = 'left' }) => (
  <Icon className="w-3.5 h-3.5">
    {direction === 'left' ? (
      <polyline points="15 18 9 12 15 6" />
    ) : (
      <polyline points="9 18 15 12 9 6" />
    )}
  </Icon>
);

const MenuIcon = () => (
  <Icon>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </Icon>
);

const CloseIcon = () => (
  <Icon>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </Icon>
);

const SidebarLink = ({ to, label, icon, collapsed, onNavigate }) => (
  <NavLink
    to={to}
    end
    onClick={onNavigate}
    title={collapsed ? label : undefined}
    className={({ isActive }) =>
      `flex items-center rounded-lg text-sm transition-colors ${
        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
      } ${
        isActive
          ? 'bg-blue-50 text-blue-700 font-medium'
          : 'text-gray-700 hover:bg-gray-50'
      }`
    }
  >
    <span className="shrink-0">{icon}</span>
    {!collapsed && <span className="truncate">{label}</span>}
  </NavLink>
);

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    writeCollapsed(collapsed);
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((prev) => !prev), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const sidebarWidthClass = collapsed ? 'md:w-16' : 'md:w-60';

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 text-gray-900" style={{ colorScheme: 'light' }}>
      {/* Overlay for mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-60 shrink-0 bg-white border-r border-gray-200 flex flex-col
          transform transition-[transform,width] duration-200 ease-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:static md:translate-x-0 md:z-auto ${sidebarWidthClass}
        `}
      >
        <div
          className={`border-b border-gray-200 flex items-center ${
            collapsed ? 'justify-center px-2 py-4' : 'justify-between px-4 py-4'
          }`}
        >
          {collapsed ? (
            <div
              className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold"
              title="Arrow.AI Admin"
            >
              A
            </div>
          ) : (
            <div>
              <div className="text-base font-semibold text-gray-900">Arrow.AI</div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-0.5">
                Admin Console
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={closeMobile}
            className="md:hidden p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className={`flex-1 py-4 space-y-1 ${collapsed ? 'px-2' : 'px-3'}`}>
          <SidebarLink
            to="/admin/dashboard"
            label="Dashboard"
            icon={<DashboardIcon />}
            collapsed={collapsed}
            onNavigate={closeMobile}
          />
          <SidebarLink
            to="/admin/models"
            label="Models"
            icon={<ModelsIcon />}
            collapsed={collapsed}
            onNavigate={closeMobile}
          />
          <SidebarLink
            to="/admin/giving"
            label="Giving"
            icon={<GivingIcon />}
            collapsed={collapsed}
            onNavigate={closeMobile}
          />
        </nav>

        <div className="border-t border-gray-200">
          <button
            type="button"
            onClick={toggleCollapsed}
            className={`hidden md:flex w-full items-center gap-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors ${
              collapsed ? 'justify-center px-2 py-3' : 'justify-start px-4 py-2.5'
            }`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronIcon direction={collapsed ? 'right' : 'left'} />
            {!collapsed && <span>Collapse</span>}
          </button>

          {!collapsed && (
            <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
              <div className="truncate">{auth.currentUser?.email}</div>
              <Link to="/" className="mt-1 inline-block text-blue-600 hover:underline">
                Back to app
              </Link>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={openMobile}
            className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
            aria-label="Open menu"
          >
            <MenuIcon />
          </button>
          <div className="text-sm font-semibold text-gray-900">Admin</div>
        </div>

        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
